import { useMemo, useRef, useState } from 'react'
import { Send } from 'lucide-react'
import { IconButton } from '../ui/IconButton.jsx'
import { cn } from '../../lib/cn.js'

/**
 * Single-line text composer. Focus management mirrors v1: focus the
 * input on mount, on send, and after the last bot message arrives.
 * Shift-Enter is reserved for future multi-line support; the current
 * legacy widget only ships a single-line input.
 */
export function Composer({
  onSend,
  disabled,
  placeholder = 'Type a message…',
  conf = null,
}) {
  const [value, setValue] = useState('')
  const inputRef = useRef(null)

  const inputStyle = useMemo(() => {
    const s = {}
    const bg = conf?.widgetInputBackgroundColor?.trim?.()
    const fg = conf?.widgetInputTextColor?.trim?.()
    if (bg) s.backgroundColor = bg
    if (fg) s.color = fg
    return Object.keys(s).length ? s : undefined
  }, [conf?.widgetInputBackgroundColor, conf?.widgetInputTextColor])

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue('')
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
      className={cn(
        'flex items-center gap-2 border-t border-solid border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2',
        'shadow-[0_-10px_18px_-18px_rgba(0,0,0,0.35)]',
      )}
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
        className={cn(
          'flex-1 rounded-full border border-solid border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[16px]',
          'text-[var(--color-surface-foreground)] placeholder:text-[var(--color-muted-foreground)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      />
      <IconButton
        type="submit"
        aria-label="Send message"
        disabled={disabled || value.trim().length === 0}
        className={cn(
          'border-transparent bg-[var(--color-brand)] text-[var(--color-brand-foreground)] shadow-sm',
          'hover:bg-[var(--color-brand)] hover:opacity-90',
          'disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        <Send className="h-4 w-4" />
      </IconButton>
    </form>
  )
}
